import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

function ForgotPasswordForm() {
  const recaptchaRef = useRef();
  const [form, setForm] = useState({ email: '', companyCode: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.email) {
      setError('Please enter your email.');
      return;
    }
    if (!form.companyCode) {
      setError('Please enter your company code.');
      return;
    }
    if (!captchaToken) {
      setError('Please complete the CAPTCHA.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        'https://api.insightos.co.uk/api/forgot-password',
        {
          email: form.email,
          companyCode: form.companyCode,
          captchaToken: captchaToken,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setError('✅ Request received. Use your authenticator app code on the next reset step.');
        setForm({ email: '', companyCode: '' });
        setCaptchaToken(null);
        recaptchaRef.current.reset();
      } else {
        setError('Failed to send reset link. Please check your email and company code.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        backgroundImage: "url('/background.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        paddingBottom: '30px',
      }}
    >
      <div className="w-full max-w-md bg-violet-800/10 rounded-xl shadow-lg p-10 backdrop-blur-md mx-4">
        <div className="flex flex-col items-center">
          <img src="1.png" alt="StaffShark" className="h-16 mb-4" />
          <h2 className="text-3xl font-extrabold text-violet-800 mb-2">
            Reset Password
          </h2>
          <p className="text-indigo-600 mb-6 text-sm">
            Enter your email and company code, then verify with your authenticator app
          </p>
          <Link
            to="/"
            className="text-sm text-indigo-600 hover:text-yellow-300 transition"
          >
            Back to Sign In
          </Link>
        </div>
        <form className="space-y-6" onSubmit={handleForgotPassword}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-violet-800">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={handleChange}
              className="mt-2 w-full rounded-md bg-violet-800/20 py-2 px-3 text-indigo-900 placeholder-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="your-email@example.com"
            />
          </div>
          <div>
            <label htmlFor="companyCode" className="block text-sm font-medium text-violet-800">
              Company Code
            </label>
            <input
              id="companyCode"
              name="companyCode"
              type="number"
              required
              value={form.companyCode}
              onChange={handleChange}
              className="mt-2 w-full rounded-md bg-violet-800/20 py-2 px-3 text-violet-800 placeholder-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter your company code"
            />
          </div>
          <div className="flex justify-center mt-4 mb-4">
            <ReCAPTCHA
              sitekey="6LeEdM8rAAAAANDYuqmGgLkqw9MmSv_2TzTEai6Q"
              ref={recaptchaRef}
              onChange={setCaptchaToken} // <-- gets the token
            />
          </div>
          {error && (
            <div className="text-red-500 text-center text-sm">{error}</div>
          )}
          <div>
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2 px-4 rounded-md font-semibold text-white bg-emerald-500 hover:bg-emerald-600 transition-all shadow-md ${
                loading ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Sending...' : 'Start Reset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ForgotPasswordForm;
